import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminPositionsService } from '../services/admin.positions.service'
import type { PositionDocument } from '@/types'

type Position = PositionDocument & { id: string }

interface UseAdminPositionsResult {
  positions: Position[]
  loading: boolean
  error: string | null
  createPosition: (title: string, order: number, group?: string) => Promise<string>
  updatePosition: (
    id: string,
    data: Partial<Pick<PositionDocument, 'title' | 'order' | 'isActive'>> & { group?: string | null }
  ) => Promise<void>
  deletePosition: (id: string) => Promise<void>
  refresh: () => void
}

export function useAdminPositions(): UseAdminPositionsResult {
  const [positions, setPositions] = useState<Position[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    adminPositionsService
      .getAllPositions()
      .then(setPositions)
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load positions'))
      )
      .finally(() => setLoading(false))
  }, [tick])

  const createPosition = useCallback(
    async (title: string, order: number, group?: string): Promise<string> => {
      const id = await adminPositionsService.createPosition(title, order, group)
      const now = new Date()
      setPositions((prev) => [
        ...prev,
        {
          id,
          title,
          order,
          isActive: true,
          ...(group ? { group } : {}),
          createdAt: now,
          updatedAt: now,
        },
      ])
      return id
    },
    []
  )

  const updatePosition = useCallback(
    async (
      id: string,
      data: Partial<Pick<PositionDocument, 'title' | 'order' | 'isActive'>> & { group?: string | null }
    ): Promise<void> => {
      let snapshot: Position | undefined
      setPositions((prev) => {
        snapshot = prev.find((p) => p.id === id)
        return prev.map((p) => {
          if (p.id !== id) return p
          const { group, ...rest } = data
          return {
            ...p,
            ...rest,
            ...(group !== undefined ? { group: group ?? undefined } : {}),
          }
        })
      })
      try {
        await adminPositionsService.updatePosition(id, data)
      } catch (err) {
        if (snapshot) {
          const saved = snapshot
          setPositions((prev) => prev.map((p) => (p.id === id ? saved : p)))
        }
        throw err
      }
    },
    []
  )

  const deletePosition = useCallback(
    async (id: string): Promise<void> => {
      let snapshot: Position | undefined
      setPositions((prev) => {
        snapshot = prev.find((p) => p.id === id)
        return prev.filter((p) => p.id !== id)
      })
      try {
        await adminPositionsService.deletePosition(id)
      } catch (err) {
        if (snapshot) {
          const saved = snapshot
          setPositions((prev) => [...prev, saved].sort((a, b) => a.order - b.order))
        }
        throw err
      }
    },
    []
  )

  return { positions, loading, error, createPosition, updatePosition, deletePosition, refresh }
}
